// components/Navbar.jsx
import { useState } from "react";
import { Link } from "react-router-dom";
import { FaBars, FaTimes } from "react-icons/fa";
import logo from "../assets/images/logo.jpg";

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  return (
    <nav className="bg-primaryBlue text-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-20 md:h-24">
          {/* Logo + Site Title */}
          <Link 
            to="/" 
            className="flex items-center space-x-4 md:space-x-6 group flex-shrink-0"
            onClick={closeMenu}
          >
            <div className="relative">
              <img 
                src={logo} 
                alt="Rising to Greatness Logo" 
                className="h-12 w-12 md:h-16 md:w-16 object-contain rounded-full border-2 border-transparent group-hover:border-primaryGold transition-all duration-300"
              />
              <div className="absolute inset-0 bg-primaryGold opacity-0 group-hover:opacity-20 rounded-full transition-opacity duration-300"></div>
            </div>
            <div className="flex flex-col">
              <h1 className="text-lg md:text-2xl font-bold leading-tight whitespace-nowrap">
                <span className="text-primaryGold group-hover:text-yellow-300 transition-colors duration-300">Rising</span>
                <span className="text-white group-hover:text-gray-200 transition-colors duration-300"> to Greatness</span>
              </h1>
              <span className="text-[11px] text-gray-300 hidden md:block mt-0.5">Community Based Organization</span>
            </div>
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center justify-center flex-1 px-4">
            <div className="flex items-center gap-x-1 xl:gap-x-2">
              <NavLink to="/">Home</NavLink>
              <NavLink to="/about">About</NavLink>
              <NavLink to="/thematic-pillars">Pillars</NavLink>
              <NavLink to="/projects">Projects</NavLink>
              <NavLink to="/impact-stories">Stories</NavLink>
              <NavLink to="/gallery">Gallery</NavLink>
              <NavLink to="/resources">Resources</NavLink>
              <NavLink to="/media">Media</NavLink>
              <NavLink to="/blog">Blog</NavLink>
              <NavLink to="/team">Team</NavLink>
              <NavLink to="/partners">Partners</NavLink>
              <NavLink to="/contact">Contact</NavLink>
            </div>
          </div>

          {/* Donate Button */}
          <div className="hidden lg:flex items-center flex-shrink-0">
            <Link
              to="/donate"
              className="bg-primaryGold text-primaryBlue px-5 md:px-6 py-2.5 rounded-lg font-semibold hover:bg-yellow-400 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 whitespace-nowrap text-sm md:text-base"
              onClick={closeMenu}
            >
              Donate
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button 
            className="lg:hidden text-2xl focus:outline-none hover:text-primaryGold transition-colors duration-300 p-2 ml-auto"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-primaryBlue border-t border-primaryGold/30 px-4 py-4 space-y-3 font-medium animate-slideDown max-h-[80vh] overflow-y-auto">
          <MobileNavLink to="/" onClick={closeMenu}>Home</MobileNavLink>
          <MobileNavLink to="/about" onClick={closeMenu}>About</MobileNavLink>
          <MobileNavLink to="/thematic-pillars" onClick={closeMenu}>Thematic Pillars</MobileNavLink>
          <MobileNavLink to="/projects" onClick={closeMenu}>Projects</MobileNavLink>
          <MobileNavLink to="/impact-stories" onClick={closeMenu}>Impact Stories</MobileNavLink>
          <MobileNavLink to="/gallery" onClick={closeMenu}>Gallery</MobileNavLink>
          <MobileNavLink to="/resources" onClick={closeMenu}>Resources</MobileNavLink>
          <MobileNavLink to="/media" onClick={closeMenu}>Media</MobileNavLink>
          <MobileNavLink to="/blog" onClick={closeMenu}>Blog</MobileNavLink>
          <MobileNavLink to="/team" onClick={closeMenu}>Team</MobileNavLink>
          <MobileNavLink to="/partners" onClick={closeMenu}>Partners</MobileNavLink>
          <MobileNavLink to="/contact" onClick={closeMenu}>Contact</MobileNavLink>
          <Link
            to="/donate"
            onClick={closeMenu}
            className="block bg-primaryGold text-primaryBlue px-4 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-all duration-300 text-center mt-4 shadow-md"
          >
            Donate
          </Link>
        </div>
      )}
    </nav>
  );
}

// Reusable NavLink component - Larger text and padding
const NavLink = ({ to, children }) => (
  <Link
    to={to}
    className="px-2 xl:px-2.5 py-2 text-white hover:text-primaryGold transition-all duration-300 font-medium relative group whitespace-nowrap text-sm xl:text-base"
  >
    {children}
    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primaryGold group-hover:w-full transition-all duration-300"></span>
  </Link>
);

// Reusable MobileNavLink component
const MobileNavLink = ({ to, onClick, children }) => (
  <Link
    to={to}
    onClick={onClick}
    className="block py-3 px-4 text-white hover:text-primaryGold hover:bg-primaryBlue/80 rounded-lg transition-all duration-300 font-medium"
  >
    {children}
  </Link>
);

export default Navbar;